// @flow

import React, {useEffect, useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import MDEditor from '@uiw/react-md-editor';
import {makeStyles} from '@material-ui/styles';

import {colors} from '../../../../../assets/styles/colors';

const useStyles = props =>
  makeStyles(theme => ({
    editorText: {
      // minHeight: '200px',
      minHeight:
        props && props.height
          ? `${props.height}px!important`
          : '100%!important',
      height: props && props.adaptable ? '100%!important' : '',
      position: 'static',
      boxShadow: props && props.border ? 'initial' : 'none!important',
      '& .w-md-editor-toolbar': {
        width: 'fit-content',
        display: props && props.selected ? 'flex' : 'none',
        position:
          props && props.selected
            ? props.isSticky
              ? 'fixed'
              : 'absolute'
            : 'static',
        top: props && props.selected ? (props.isSticky ? 72 : 8) : 'unset',
        left: props && props.selected ? (props.isSticky ? 332.5 : 16) : 'unset',
        zIndex: props && props.isSticky ? '999' : '0',
        height: '35px!important',
        borderBottom: 'none',
        backgroundColor: 'unset',
        '& li:nth-of-type(10)': {
          display: 'none!important',
        },
        '& li:nth-of-type(11)': {
          display: 'none!important',
        },
        '& li:nth-of-type(12)': {
          display: 'none!important',
        },
        '& ul li button': {
          backgroundColor: '#FFFFFF',
        },
        [theme.breakpoints.down('sm')]: {
          height: 'fit-content!important',
          padding: theme.spacing(1),
          flexWrap: 'wrap',
          position: 'fixed',
          zIndex: '9',
          top: 'unset',
          left: 0,
          bottom: 64,
          width: '100%!important',
          backgroundColor: colors.grey200,
          '& ul li button': {
            height: 35,
            width: 35,
          },
        },
        '& ul:last-of-type': {
          display: 'none!important',
        },
      },

      '& .w-md-editor-content': {
        height: '100%!important',
        padding: theme.spacing(1),
        '& > div': {
          backgroundColor: '#FFFFFF',
        },
      },
      '& .w-md-editor-text-input ': {
        fontFamily: '"Poppins", "Muli", sans-serif !important',
        color: colors.grey600,
        fontSize: 14,
        fontWeight: 500,
      },
      '& .w-md-editor-content > div > div > div > pre > code': {
        fontFamily: '"Poppins", "Muli", sans-serif !important',
        fontWeight: 500,
      },
      '& .w-md-editor-content > div > div > div > pre > code > span .italic': {
        fontStyle: 'unset',
      },
    },
    sticky: {
      '& .w-md-editor-toolbar': {
        width: 'fit-content',
        backgroundColor: colors.grey200,
        padding: theme.spacing(2, 1),
      },
    },
  }));

type Props = {
  idx: number,
  adaptable?: boolean,
  noBorder?: boolean,
  text: string,
  stickyToolbar?: boolean,
  placeholder?: string,
  height?: number,
  onChange: (str: string, idx: number) => void,
  handleInputFocused?: (idx: number) => void,
  selected?: boolean,
  disabled?: boolean,
  classes?: {
    editorText?: string,
  },
};

export default function CustomEditor(props: Props) {
  const {
    idx,
    adaptable = false,
    noBorder = false,
    stickyToolbar = false,
    handleInputFocused,
    height,
    text,
    onChange,
    placeholder,
    disabled = false,
    selected,
    classes: classesProp = {},
  } = props;

  const [isSticky, setIsSticky] = useState(false);
  const classes = useStyles({
    selected: !!selected,
    height: height,
    border: !noBorder,
    adaptable: !!adaptable,
    isSticky,
  })();

  const handleFocus = () => {
    if (handleInputFocused) handleInputFocused(idx);
  };

  const handleChange = str => onChange(str, idx);

  const handleSticky = useCallback(() => {
    if (stickyToolbar) {
      const element = document.getElementById('container-toolbar');
      if (element) {
        const top = element.getBoundingClientRect().top;
        if (top < 80) {
          setIsSticky(true);
        } else setIsSticky(false);
      }
    }
  }, [stickyToolbar]);

  useEffect(() => {
    if (stickyToolbar && window) {
      window.addEventListener('scroll', handleSticky);
      return () => window.removeEventListener('scroll', handleSticky);
    }
  }, [handleSticky, stickyToolbar]);

  return (
    <MDEditor
      textareaProps={{
        onFocus: handleFocus,
        disabled: disabled,
        placeholder: placeholder ? placeholder : 'Texto descriptivo',
      }}
      height={height ? height : 168}
      preview="edit"
      visibleDragbar={false}
      className={`${classes.editorText} ${
        isSticky ? classes.sticky : ''
      } ${classesProp.editorText || ''}`}
      value={text}
      disabled={disabled}
      onChange={handleChange}
    />
  );
}

CustomEditor.propTypes = {
  idx: PropTypes.number.isRequired,
  adaptable: PropTypes.bool,
  noBorder: PropTypes.bool,
  stickyToolbar: PropTypes.bool,
  text: PropTypes.string.isRequired,
  height: PropTypes.number,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  handleInputFocused: PropTypes.func,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
};
